import * as React from 'react';
import { DataTable } from 'primereact/datatable';
import { useContext, useEffect, useRef, useState } from 'react';
import { ValueLabel } from '../../shared/models';
import { AppContext } from '../../store/AppContext';
import Label from '../../components/Label';
import SelectInput from '../../components/SelectInput';
import _ from 'lodash';
import * as util from '../../shared/Utilities';
import { ProductEntityModel, ProductGridModel } from './models';
import { convertProductEntityToGridModel, getAllConnectorLabels, getImportedProductsDataSnapshot, getSuccessSyncProducts } from './services/ProductService';
import { getImportFailedProductsApproach1 } from '../../dataServices/ProductDataService';
import moment from 'moment';
import { TabPanel, TabView } from 'primereact/tabview';
import ProductGridErrorComponent from './components/ProductGridErrorComponent';
import ProductGridComponent from './components/ProductGridComponent';
import myobToNeto from "../../assets/myobToNeto.png"
import netoToMyob from "../../assets/netoToMyob.png"
import netoToMyob2Way from "../../assets/netoToMyob2Way.png"
import { getConnectorById } from '../../dataServices/ConnectorDataService';

export const useDebouncedEffect = (effect: any, deps: any, delay: number) => {
  useEffect(() => {
    const handler = setTimeout(() => effect(), delay);

    return () => clearTimeout(handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps || [], delay]);
}

const ProductGridContainer: React.FC = () => {
  let { State, Dispatch } = useContext(AppContext);
  const dt = useRef<DataTable>(null);
  const [connectors, setConnectors] = useState<Array<ValueLabel>>([{ label: "", value: "" }]);
  const [selectedConnector, setSelectedConnector] = useState<string>(State.selectedConnectorOnGrid ?? "");
  const [productData, setProductData] = useState<Array<ProductGridModel>>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [fLoading, setFLoading] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<number>(0);
  const [failedProductsData, setFailedProductsData] = useState<Array<ProductGridModel>>([]);
  const [lastRecordId, setLastRecordId] = useState<string>();
  const [firstRecordId, setFirstRecordId] = useState<string>();
  const [failedFirstRecordId, setFailedFirstRecordId] = useState<string>('');
  const [failedLastRecordId, setFailedLastRecordId] = useState<string>('');
  const [isNextDisabled, setIsNextDisabled] = useState<boolean>(false);
  const [isPrevDisabled, setIsPrevDisabled] = useState<boolean>(false);
  const [isFailedOrderNextDisabled, setIsFailedOrderNextDisabled] = useState<boolean>(false);
  const [isFailedOrderPrevDisabled, setIsFailedOrderPrevDisabled] = useState<boolean>(false);
  const [loadedTabs, setLoadedTabs] = useState<Record<number, boolean>>({ 0: false, 1: false });
  const [isCalledProducts, setIsCalledProducts] = useState<boolean>(false);
  const [netoToMyobSync, setNetoToMyobSync] = useState(false);
  const [myobToNetoSync, setMyobToNetoSync] = useState(false);

  const [productsLazyParams, setProductsLazyParams] = useState({
    first: 0, rows: 5, page: 1, sortField: "lastSyncAt",
    sortOrder: -1, filters: undefined, pageMode: '', pageSize: 25,
  });

  const [failedProductsLazyParams, setFailedProductsLazyParams] = useState({
    first: 0, rows: 5, page: 1, sortField: "lastSyncAt",
    sortOrder: -1, filters: undefined, pageMode: '', pageSize: 15,
  });

  const filterFields = {
    SKU: 'neto.SKU',
    Number: 'myob.Number',
    NetoName: 'neto.Name',
    MyobName: 'myob.Name',
    IsInventoriedNeto: 'neto.Virtual', //'neto.IsInventoried',
    IsInventoriedMyob: 'myob.IsInventoried',
    syncErrorMessageNeto: 'neto.syncErrorMessage',
    syncErrorMessageMyob: 'myob.syncErrorMessage',
    lastSyncAt: 'lastSyncAt',
  }

  const handleTabChange = (e: any) => {
    const newTabIndex = e?.index;
    setActiveTab(newTabIndex);
    // Only fetch data if this tab hasn't been loaded before
    if (!loadedTabs[newTabIndex] && selectedConnector) {
      if (newTabIndex === 0) {
        fetchFailedProducts();
      } else {
        fetchSuccessProducts();
      }
      // Mark tab as loaded
      setLoadedTabs(prev => ({
        ...prev,
        [newTabIndex]: true
      }));
    }
  };

  useEffect(() => {
    (async () => {
      if (selectedConnector == "") return;
      const connector = await getConnectorById(State.currentUser.currentUserId, selectedConnector);
      if (connector?.isSucceed) {
        const connectorData = connector?.result;
        const userMappings = connectorData?.mappings;
        setNetoToMyobSync(userMappings?.itemMapping?.isChecked ?? false)
        setMyobToNetoSync(userMappings?.productStockMapping?.isChecked ?? false)
      }
    })();

  }, [selectedConnector]);

  useEffect(() => {
    const loadConnectors = async () => {
      setLoading(true);
      const connectorsList = await getAllConnectorLabels(State.currentUser.currentUserId);
      setConnectors(connectorsList);
      setLoading(false);
      if (State.currentUser.currentUserId) {
        setSelectedConnector(connectorsList?.length && connectorsList?.[0].value ? connectorsList?.[0].value : "");
      }
    }

    if (State?.currentUser?.currentUserId) {
      loadConnectors();
    };
    return () => {
      Dispatch({
        Type: "SET_SALES_ORDER",
        Payload: { connector: "", data: [] },
      });
    };
  }, [State?.currentUser?.currentUserId]);


  useEffect(() => {
    setProductsLazyParams({
      first: 0,
      rows: 5,
      page: 1,
      sortField: "lastSyncAt",
      sortOrder: -1,
      filters: undefined,
      pageMode: '',
      pageSize: 25,
    });
    setFailedProductsLazyParams({
      first: 0,
      rows: 5,
      page: 1,
      sortField: "lastSyncAt",
      sortOrder: -1,
      filters: undefined,
      pageMode: '',
      pageSize: 15,
    });
    setProductData([]);
    setFailedProductsData([]);
  }, [selectedConnector]);

  useDebouncedEffect(() => {
    if (selectedConnector && !isCalledProducts) {
      setLoading(true);
      setIsCalledProducts(false);
      // Reset loaded tabs and data when connector changes
      setLoadedTabs({ 0: false, 1: false });
      setProductData([]);
      setFailedProductsData([]);
      // Load data for the current tab
      if (activeTab === 0) {
        fetchFailedProducts();
      } else {
        fetchSuccessProducts();
      }
    };

    if (!_.isEmpty(selectedConnector) && isCalledProducts) {
      triggerFetchData();
    }
  }, [productsLazyParams, failedProductsLazyParams, selectedConnector], 1000);

  // useDebouncedEffect(() => { setLoading(true); fetchSuccessProducts(); }, [productsLazyParams], 1000);



  const fetchSuccessProducts = () => {
    if (State?.currentUser?.currentUserId && selectedConnector) {
      let filtersArray: { key: string; value: any; }[] = [];
      if (productsLazyParams?.filters) {
        const filters: any = productsLazyParams.filters;
        if (filters['NetoProduct.SKU']) {
          if (!_.isEmpty(filters['NetoProduct.SKU']?.value))
            filtersArray.push({ key: filterFields.SKU, value: filters['NetoProduct.SKU']?.value })
        }

        if (filters['MyobProduct.Number']) {
          if (!_.isEmpty(filters['MyobProduct.Number']?.value))
            filtersArray.push({ key: filterFields.Number, value: filters['MyobProduct.Number']?.value })
        }

        if (filters['NetoProduct.Name']) {
          if (!_.isEmpty(filters['NetoProduct.Name']?.value))
            filtersArray.push({ key: filterFields.NetoName, value: filters['NetoProduct.Name']?.value })
        }

        if (filters['MyobProduct.Name']) {
          if (!_.isEmpty(filters['MyobProduct.Name']?.value))
            filtersArray.push({ key: filterFields.MyobName, value: filters['MyobProduct.Name']?.value })
        }

        if (filters['NetoProduct.IsInventoriedNeto']?.value) {
          if (!_.isEmpty(filters['NetoProduct.IsInventoriedNeto']?.value))
            filtersArray.push({
              key: filterFields.IsInventoriedNeto, value: filters['NetoProduct.IsInventoriedNeto']?.value
                === 'Inventoried' ? 'False' : 'True'
            }) //['y', 'True'] : ['n', 'False']})
        }

        if (filters['MyobProduct.IsInventoriedMyob']?.value) {
          if (!_.isEmpty(filters['MyobProduct.IsInventoriedMyob']?.value))
            filtersArray.push({ key: filterFields.IsInventoriedMyob, value: filters['MyobProduct.IsInventoriedMyob']?.value === 'Inventoried' ? true : false })
        }

        if (filters['NetoProduct.syncErrorMessage']?.value) {
          if (!_.isEmpty(filters['NetoProduct.syncErrorMessage']?.value))
            filtersArray.push({ key: filterFields.syncErrorMessageNeto, value: filters['NetoProduct.syncErrorMessage']?.value })
        }

        if (filters['MyobProduct.syncErrorMessage']?.value) {
          if (!_.isEmpty(filters['MyobProduct.syncErrorMessage']?.value))
            filtersArray.push({ key: filterFields.syncErrorMessageMyob, value: filters['MyobProduct.syncErrorMessage']?.value })
        }

        if (filters['lastSyncAt']) {
          if ((filters?.lastSyncAt?.value))
            // filtersArray.push({ key: filterFields.lastSyncAt, value: filters['lastSyncAt']?.value })
            filtersArray.push({ key: filterFields.lastSyncAt, value: util.getUserTimezoneStartAndEndOfADay(moment(filters?.lastSyncAt?.value)?.format('YYYY-MM-DD')) });
        }
      }

      getSuccessSyncProducts(State.currentUser.currentUserId, selectedConnector,
        productsLazyParams.pageSize, productsLazyParams?.sortField, productsLazyParams?.sortOrder,
        firstRecordId, lastRecordId, productsLazyParams.pageMode, filtersArray).subscribe((e: any) => {

          let products: ProductGridModel[] = [];
          e?.products && _.size(e?.products) > 0 && e?.products?.map((doc: any, index: number) => {
            let userData: ProductEntityModel = doc;
            products.push(convertProductEntityToGridModel(userData, index));
          });

          if (e?.products && e?.products?.length > 0) {
            setProductData(products);
            setFirstRecordId(e?.firstDocId);
            setLastRecordId(e?.lastDocId);

            if (productsLazyParams.pageMode === 'ALL') {
              setIsNextDisabled(true);
              setIsPrevDisabled(true);
            } else {
              setIsNextDisabled(false);
              setIsPrevDisabled(false);
            }

            setLoading(false);
          }
          else {
            if (productsLazyParams.pageMode === 'ALL') {
              setProductData([]);
              setIsNextDisabled(true);
              setIsPrevDisabled(true);
            } else if (productsLazyParams.pageMode === 'PREV') {
              setIsPrevDisabled(true);
              setFirstRecordId("");
            } else if (productsLazyParams.pageMode === 'NEXT') {
              setIsNextDisabled(true);
              setLastRecordId("");
            } else {
              setProductData([]);
              setIsNextDisabled(true);
              setIsPrevDisabled(true);
            }

            setLoading(false);
          }
        });
      util.getTimezoneFromFirebase(State?.currentUser?.isAdminUser && localStorage.SelectedIdByAdmin ? localStorage.SelectedIdByAdmin : State.currentUser.currentUserId, selectedConnector);
    }
  }

  const fetchFailedProducts = () => {
    setFLoading(true);
    if (!_.isEmpty(selectedConnector)) {
      getImportFailedProductsApproach1(State.currentUser.currentUserId, selectedConnector).subscribe((e: any) => {
        let products: any = [];
        e?.map((doc: any, index: number) => {
          const userData: ProductEntityModel = doc?.data();
          products.push(convertProductEntityToGridModel(userData, index))
        });

        setFailedProductsData(products);
        setFLoading(false);
      });
    } else {
      setFLoading(false);
    }
  }

  const triggerFetchData = () => {
    if (activeTab === 1) {
      setLoading(true);
      fetchSuccessProducts();
    } else if (activeTab === 0) {
      setLoading(true);
      fetchFailedProducts();
    }
    setLoadedTabs(prev => ({
      ...prev,
      [activeTab]: true
    }));
  }

  return (
    <div className="container-fluid">
      <div className="row mt-5">
        <div className="col-3">
          <Label text={"Select Connector: "} />
        </div>
        <div className="col-9">
          <SelectInput
            options={connectors}
            selectedOptionValue={selectedConnector}
            onChange={(connector: string) => {
              Dispatch({
                Type: "SET_SELECTED_CONNECTOR",
                Payload: {
                  id: connector
                },
              });
              setSelectedConnector(connector)
            }}
          />
        </div>
      </div>

      <div className="container mt-5">
        {myobToNetoSync && !netoToMyobSync && <img src={myobToNeto} alt="myobToNeto" height="30rem" />}
        {myobToNetoSync && netoToMyobSync && <img src={netoToMyob2Way} alt="netoToMyob2Way" height="30rem" />}
        {!myobToNetoSync && netoToMyobSync && <img src={netoToMyob} alt="netoToMyob" height="30rem" />}
      </div>

      <TabView activeIndex={activeTab} onTabChange={(e) => handleTabChange(e)}>
        <TabPanel header={<b>Failed Products</b>}>
          <ProductGridErrorComponent
            loading={fLoading}
            productData={failedProductsData}
          // setLoading={setFLoading} 
          />
        </TabPanel>
        <TabPanel header={<b>Synced Products</b>}>
          <ProductGridComponent
            productData={productData}
            isNextDisabled={isNextDisabled}
            isPrevDisabled={isPrevDisabled}
            lazyParams={productsLazyParams}
            loading={loading}
            setLoading={setLoading}
            selectedConnector={selectedConnector}
            setLazyParams={setProductsLazyParams}
            setStartAfterRecord={setFirstRecordId}
            setStartBeforeRecord={setLastRecordId}
          />
        </TabPanel>
      </TabView>
    </div>
  )
}

export default ProductGridContainer